import React, { useState, useEffect } from 'react';
import { recommendationsApi, RecommendationStats } from '../../services/recommendations';

export const RecommendationAnalytics: React.FC = () => {
  const [stats, setStats] = useState<RecommendationStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await recommendationsApi.getStats();
        setStats(data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <div className="text-center py-8">Loading analytics...</div>;
  }

  if (!stats) {
    return <div className="text-center py-8">Failed to load analytics</div>;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Recommendation Analytics</h1>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-gray-600 text-sm">Total Recommendations</div>
          <div className="text-3xl font-bold text-blue-600">{stats.total_recommendations}</div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-gray-600 text-sm">Unique Travelers</div>
          <div className="text-3xl font-bold text-green-600">{stats.unique_travelers}</div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-gray-600 text-sm">Success Rate</div>
          <div className="text-3xl font-bold text-purple-600">
            {(stats.validation_success_rate * 100).toFixed(1)}%
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-gray-600 text-sm">Avg Input Tokens</div>
          <div className="text-3xl font-bold text-orange-600">
            {Math.round(stats.avg_input_tokens).toLocaleString()}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-gray-600 text-sm">Avg Output Tokens</div>
          <div className="text-3xl font-bold text-red-600">
            {Math.round(stats.avg_output_tokens).toLocaleString()}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-gray-600 text-sm">High Risk Cases</div>
          <div className="text-3xl font-bold text-yellow-600">{stats.high_risk_cases}</div>
        </div>
      </div>

      {/* Provider Breakdown */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium mb-4">Provider Performance</h3>
        <div className="space-y-3">
          {stats.provider_breakdown.map((provider, index) => (
            <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <div>
                <div className="font-medium">{provider.provider}</div>
                <div className="text-sm text-gray-600">{provider.count} recommendations</div>
              </div>
              <div className="text-right">
                <div className="font-medium">{Math.round(provider.avg_tokens).toLocaleString()} avg tokens</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};